import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Modal, Space, Tag, Typography, message
} from 'antd';
import {
  SafetyOutlined,
  ReloadOutlined,
  CopyOutlined,
  PrinterOutlined,
  DownloadOutlined,
  KeyOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useAppNotifications, parseApiError } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';

// Stage 5M-Recovery — tenant MFA recovery codes admin surface.
//
// Mounted next to MasterMfaSection on the master's "Authenticator"
// tab. Shows the current batch status (unused / used / last generated
// at). Clicking "Regenerate" calls the backend, displays the 10
// plaintext codes in a one-shot modal (with copy / print / download
// affordances) and warns the master that the codes are shown only
// once.
//
// SECURITY
//   - The plaintext codes are kept only in React state for the lifetime
//     of this modal. Closing the modal flushes them and never returns.
//   - We do not log the codes, do not put them in localStorage / sessionStorage,
//     and the download artifact is a Blob created at click-time only.
//   - The /api/mfa/recovery-codes/regenerate endpoint sets
//     Cache-Control: no-store so an intermediate cache can't replay
//     the response body.

const { Title, Paragraph, Text } = Typography;

function CodeBlock({ codes }) {
  // Render the 10 codes as a monospaced grid. Each is XXXX-XXXX so
  // copying one code at a time stays easy on touch devices.
  return (
    <div className="grid grid-cols-2 gap-2 my-3 p-3 rounded-lg bg-surface-50 border border-surface-200 font-mono text-[15px] tracking-wider text-ink-900">
      {codes.map((c, i) => (
        <div key={c.id} className="flex items-center gap-2">
          <span className="text-ink-400 text-[11px] w-5 inline-block text-right">
            {i + 1}.
          </span>
          <span className="select-all">{c.plaintext}</span>
        </div>
      ))}
    </div>
  );
}

function buildClipboardText(codes) {
  return codes.map((c, i) => `${i + 1}. ${c.plaintext}`).join('\n');
}

function buildDownloadText(codes, generationId) {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return [
    'Glass-i — MFA recovery codes',
    `Generated at: ${new Date().toISOString()}`,
    `Generation ID: ${generationId}`,
    '',
    'Each code can be used ONCE in place of the 6-digit Authenticator code.',
    'Keep this file in a secure location (password manager, locked drawer).',
    'Re-generating from the app invalidates ALL codes below — old codes',
    'will no longer be accepted.',
    '',
    ...codes.map((c, i) => `${String(i + 1).padStart(2, ' ')}. ${c.plaintext}`),
    ''
  ].join('\n');
}

export default function RecoveryCodesSection() {
  const { notifyError, notifySuccess } = useAppNotifications();

  const [status, setStatus] = useState(null);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [codesModalOpen, setCodesModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Plaintext lives ONLY here for the modal lifetime.
  const [revealed, setRevealed] = useState(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      // Two independent reads — both master-only, both cheap. Done in
      // parallel so the section stays snappy when the tab opens.
      const [rec, mfa] = await Promise.all([
        endpoints.mfa.getRecoveryCodesStatus(),
        endpoints.mfa.getStatus()
      ]);
      setStatus(rec);
      setMfaStatus(mfa);
    } catch (err) {
      const parsed = parseApiError(err);
      if (parsed && parsed.code && parsed.code !== 'INTERNAL_ERROR') {
        notifyError(err, { title: 'Could not load recovery codes status' });
      }
      setStatus({ unused: 0, used: 0, superseded: 0, perBatch: 10, lastGeneratedAt: null });
      setMfaStatus({ isSetupComplete: false, isActive: false });
    } finally {
      setStatusLoading(false);
    }
  }, [notifyError]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const mfaSetupComplete = !!(mfaStatus && mfaStatus.isSetupComplete);
  const mfaActive        = !!(mfaStatus && mfaStatus.isActive);

  async function handleRegenerate() {
    setBusy(true);
    try {
      const data = await endpoints.mfa.regenerateRecoveryCodes();
      setRevealed(data);
      setConfirmOpen(false);
      setCodesModalOpen(true);
      await loadStatus();
      notifySuccess('Recovery codes generated', '10 new codes are ready. Old codes have been revoked.');
    } catch (err) {
      notifyError(err, { title: 'Could not generate recovery codes' });
    } finally {
      setBusy(false);
    }
  }

  async function copyAll() {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(buildClipboardText(revealed.codes));
      message.success('Codes copied to clipboard');
    } catch (_e) {
      message.error('Could not access the clipboard.');
    }
  }

  function downloadTxt() {
    if (!revealed) return;
    const blob = new Blob([buildDownloadText(revealed.codes, revealed.generationId)],
      { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `glass-i-recovery-codes-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function printCodes() {
    if (!revealed) return;
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) return;
    const rows = revealed.codes
      .map((c, i) => `<li>${String(i + 1).padStart(2, '0')}. ${c.plaintext}</li>`)
      .join('');
    w.document.write(`<!doctype html><html><head><title>Glass-i recovery codes</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:32px;color:#0f172a}
        h1{font-size:18px;margin:0 0 4px}
        .meta{color:#64748b;font-size:12px;margin-bottom:16px}
        ol{font-family:'JetBrains Mono','SF Mono',Menlo,monospace;
            font-size:15px;letter-spacing:.05em;line-height:1.9;
            padding-left:0;list-style:none}
        .note{margin-top:24px;font-size:11px;color:#475569;border-top:1px dashed #cbd5e1;padding-top:12px;}
      </style></head><body>
      <h1>Glass-i — MFA recovery codes</h1>
      <div class="meta">Generated ${new Date().toLocaleString()}</div>
      <ol>${rows}</ol>
      <p class="note">Each code can be used once in place of the 6-digit
      Authenticator code. Store this sheet in a secure place. Regenerating
      from the app invalidates all codes on this sheet.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    // Give layout a beat then print
    setTimeout(() => { try { w.print(); } catch (_) { /* noop */ } }, 120);
  }

  function closeCodesModal() {
    // Drop the plaintext the moment the modal closes. setState is
    // synchronous-enough for our purposes — the reference is replaced
    // before the next render. Browsers may keep the strings around
    // until GC; we accept that trade-off.
    setRevealed(null);
    setCodesModalOpen(false);
  }

  const unused = status ? Number(status.unused || 0) : 0;
  const used   = status ? Number(status.used   || 0) : 0;
  const perBatch = status && status.perBatch ? Number(status.perBatch) : 10;
  const everGenerated = !!(status && status.lastGeneratedAt);

  if (statusLoading) {
    return <div className="mt-6"><LoadingBlock /></div>;
  }

  return (
    <div className="mt-6 rounded-xl border border-surface-200 bg-white">
      <div className="px-4 py-3 border-b border-surface-200 flex items-center gap-2">
        <SafetyOutlined className="text-brand-600" />
        <h3 className="m-0 text-sm font-semibold text-ink-900">
          Recovery codes
        </h3>
      </div>

      <div className="p-4">
        <Paragraph className="!mb-3 text-ink-600">
          One-time codes you can use in place of the 6-digit Authenticator
          code if you lose the phone. {unused > 0 ? (
            <Text strong>
              {unused} of {perBatch} remain unused.
            </Text>
          ) : everGenerated ? (
            <Text type="warning">
              All codes have been used or revoked — regenerate to get a fresh batch.
            </Text>
          ) : (
            <Text type="warning">
              No recovery codes have been generated yet.
            </Text>
          )}
        </Paragraph>

        {!mfaSetupComplete ? (
          <Alert
            showIcon
            type="info"
            className="!mb-3"
            message="Set up the Authenticator before generating recovery codes."
            description="Recovery codes work alongside the tenant TOTP. Scan the QR above and verify it first, then come back to generate codes."
          />
        ) : !mfaActive ? (
          <Alert
            showIcon
            type="warning"
            className="!mb-3"
            message="MFA is configured but currently inactive."
            description="You can still generate recovery codes now and keep them ready. They will start working when you flip MFA to active."
          />
        ) : null}

        <Space wrap>
          <Tag color={unused > 0 ? 'success' : 'default'}>
            {unused} unused
          </Tag>
          <Tag>
            {used} used
          </Tag>
          {everGenerated ? (
            <Tag color="processing">
              Last generated{' '}
              {new Date(status.lastGeneratedAt).toLocaleString()}
            </Tag>
          ) : null}
        </Space>

        <div className="mt-4">
          <Button
            icon={everGenerated ? <ReloadOutlined /> : <KeyOutlined />}
            disabled={!mfaSetupComplete}
            onClick={() => setConfirmOpen(true)}
          >
            {everGenerated ? 'Regenerate recovery codes' : 'Generate recovery codes'}
          </Button>
        </div>
      </div>

      {/* Confirm before invalidating the previous batch. */}
      <Modal
        open={confirmOpen}
        title={
          <span className="flex items-center gap-2">
            <ExclamationCircleOutlined className="text-amber-500" />
            {everGenerated ? 'Regenerate recovery codes?' : 'Generate recovery codes?'}
          </span>
        }
        onCancel={() => setConfirmOpen(false)}
        confirmLoading={busy}
        okText={everGenerated ? 'Yes, regenerate' : 'Generate'}
        okButtonProps={{ danger: everGenerated }}
        onOk={handleRegenerate}
      >
        {everGenerated ? (
          <>
            <p>
              <Text strong>All {perBatch} codes from the previous batch will
              stop working immediately</Text>, whether they were used or not.
              You will see the new codes on the next screen.
            </p>
            <p className="text-ink-500 text-sm">
              Store the new codes somewhere safe before closing the modal.
            </p>
          </>
        ) : (
          <p>
            We'll generate {perBatch} single-use codes you can use in place
            of the 6-digit Authenticator code if you lose your phone. The
            codes will be visible on the next screen <Text strong>only once</Text> —
            print or download them before closing.
          </p>
        )}
      </Modal>

      {/* Plaintext modal — visible exactly once per generation. */}
      <Modal
        open={codesModalOpen && !!revealed}
        title={
          <span className="flex items-center gap-2">
            <SafetyOutlined className="text-emerald-600" />
            Save your recovery codes
          </span>
        }
        onCancel={closeCodesModal}
        width={520}
        footer={[
          <Button key="close" type="primary" onClick={closeCodesModal}>
            I've saved them
          </Button>
        ]}
        maskClosable={false}
      >
        <Alert
          showIcon
          type="warning"
          className="!mb-2"
          message="These codes are shown ONCE. They will not be visible again."
          description="Each code can be used once in place of the 6-digit Authenticator code. Store them in a password manager, printed and locked away, or both."
        />
        {revealed ? <CodeBlock codes={revealed.codes} /> : null}
        <Space wrap>
          <Button icon={<CopyOutlined />} onClick={copyAll}>Copy all</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTxt}>Download .txt</Button>
          <Button icon={<PrinterOutlined />} onClick={printCodes}>Print</Button>
        </Space>
      </Modal>
    </div>
  );
}
